import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer } from "recharts";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Download, CheckSquare, Clock, AlertTriangle, XCircle } from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899"];
const PRIORIDADE_COLORS = { baixa: "#94a3b8", media: "#3b82f6", alta: "#f59e0b", urgente: "#ef4444" };

export default function TarefasReport({filterHook }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const baseFilters = {
        responsavel_ids: filterHook.filters.responsavelIds,
        period: { start: filterHook.filters.periodStart + "T00:00:00Z", end: filterHook.filters.periodEnd + "T23:59:59Z", field: "created_at" },
        status: statusFilter === "all" ? [] : [statusFilter],
      };
      const res = await axios.post(`${API}/reports/tarefas`, { filters: baseFilters }, { withCredentials: true });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterHook.filters, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet((data.by_responsavel || []).map(r => ({
      "Responsável": r.responsavel, "Total": r.total, "Concluídas": r.concluidas,
      "Abertas": r.abertas, "Vencidas": r.vencidas, "Canceladas": r.canceladas, "Taxa %": r.taxa_conclusao,
    })));
    XLSX.utils.book_append_sheet(wb, ws, "Tarefas por Responsável");
    XLSX.writeFile(wb, `relatorio_tarefas_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const kpis = data ? [
    { title: "Total", value: data.total, sub: "no período", icon: CheckSquare, color: tc500 },
    { title: t("ui.concluidas"), value: data.concluidas, sub: `${data.taxa_conclusao}% de conclusão`, icon: CheckSquare, color: "#10b981" },
    { title: t("ui.abertas"), value: data.abertas, sub: "em andamento", icon: Clock, color: "#f59e0b" },
    { title: "Vencidas", value: data.vencidas, sub: "prazo excedido", icon: AlertTriangle, color: "#ef4444" },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("ui.todos_status")}</SelectItem>
              <SelectItem value="aberta">{t("ui.abertas")}</SelectItem>
              <SelectItem value="concluida">{t("ui.concluidas")}</SelectItem>
              <SelectItem value="cancelada">{t("ui.canceladas")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600">
          <Download className="w-4 h-4" />{t("relatorios.exportExcel")}</Button>
      </div>

      {loading && <div className="flex items-center justify-center h-32 text-slate-400 text-sm">{t("common.loading")}</div>}

      {!loading && data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map(k => (
              <div key={k.title} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{k.title}</p>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${k.color}18` }}>
                    <k.icon className="w-4 h-4" style={{ color: k.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 mb-1">{k.value}</p>
                <p className="text-xs text-slate-400">{k.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Por responsável */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-700">{t("dashboard.performance")}</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {(data.by_responsavel || []).slice(0,8).map((r, i) => (
                  <div key={r.responsavel} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{backgroundColor:COLORS[i%COLORS.length]}}>
                      {r.responsavel?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-sm font-medium text-slate-700 truncate">{r.responsavel}</span>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{r.total} total</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-400">
                        <span className="text-emerald-500">{r.concluidas} concluídas</span>
                        <span className="text-amber-500">{r.abertas} abertas</span>
                        {r.vencidas > 0 && <span className="text-rose-500">{r.vencidas} vencidas</span>}
                        <span className="ml-auto font-semibold text-slate-600">{r.taxa_conclusao}%</span>
                      </div>
                    </div>
                  </div>
                ))}
                {!data.by_responsavel?.length && <p className="text-center py-8 text-slate-400 text-sm">Nenhum dado encontrado.</p>}
              </div>
            </div>

            {/* Por tipo + prioridade */}
            <div className="space-y-4">
              {data.by_tipo?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">{t("ui.por_tipo")}</h3>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={data.by_tipo.slice(0,6)} margin={{top:0,right:10,left:0,bottom:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="tipo" tick={{fontSize:10}} />
                      <YAxis tick={{fontSize:10}} />
                      <RT contentStyle={{fontSize:12}} />
                      <Bar dataKey="count" fill={tc500} radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {data.by_prioridade?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Por Prioridade</h3>
                  <div className="space-y-2">
                    {data.by_prioridade.map(p => {
                      const total = data.total || 1;
                      const pct = Math.round(p.count / total * 100);
                      const color = PRIORIDADE_COLORS[p.prioridade] || "#94a3b8";
                      return (
                        <div key={p.prioridade} className="flex items-center gap-3">
                          <span className="text-xs capitalize text-slate-600 w-16 flex-shrink-0">{p.prioridade}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-2">
                            <div className="h-2 rounded-full" style={{width:`${pct}%`,backgroundColor:color}} />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 w-8 text-right">{p.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
